import { Button } from "components/buttons/button/Button";
import { Tooltip } from "components/common/tooltip";
import Form from "components/forms/layout";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { Icon, Radio } from "semantic-ui-react";
import { ValidatedForm } from "components/forms";
import { IntegrationTypes, type MsGraphIntegrationConfigs } from "../types";
import styles from "./integrationConfiguration.module.scss";
import {
  getImportGroupsTooltipMessage,
  getImportLicensesTooltipMessage,
  getImportUsageDataTooltipMessage,
  getSyncUsersTooltipMessage,
} from "./tooltipsHelpers";
import MsGraphIntegrationFilterBuilder, {
  type FilterBuilderRef,
} from "./MsGraphIntegrationFilterBuilder/MsGraphIntegrationFilterBuilder";
import cn from "classnames";
import { type Dispatch, type MutableRefObject, type SetStateAction } from "react";

export interface MsGraphIntegrationConfigurationProps {
  isCreating?: boolean;
  integrationName: string;
  setConfigs: Dispatch<SetStateAction<MsGraphIntegrationConfigs | undefined>>;
  configs: MsGraphIntegrationConfigs;
  onSyncClick?: () => void;
  setIsValid: (isValid: boolean) => void;
  filterBuilderFormRef?: MutableRefObject<FilterBuilderRef>;
}

const MsGraphIntegrationConfiguration: React.FC<MsGraphIntegrationConfigurationProps> = (props) => {
  const { integrationName, isCreating, setConfigs, configs, onSyncClick, setIsValid, filterBuilderFormRef } = props;
  const isLicensesFilterEnabled = !!useFeatureFlag(FeatureFlags.LicenseGroupsFilter);
  const isMsGraphSyncRulesEnabled = !!useFeatureFlag(FeatureFlags.MsGraphSyncRules);
  const isMsGraphAutoRemoveUsersEnabled = !!useFeatureFlag(FeatureFlags.MsGraphAutoRemoveUsers);

  // eslint-disable-next-line
  const setFieldValue = (id: string) => {
    let changedConfigsState: MsGraphIntegrationConfigs = {
      ...configs,
    };

    switch (id) {
      case "syncUsers":
        if (configs.syncUsers && !configs.importUsageData) {
          changedConfigsState.importUsageData = !configs.importUsageData;
        }
        changedConfigsState.importLicenses = !configs.syncUsers;
        changedConfigsState.importGroups = !configs.syncUsers;
        changedConfigsState.deleteInactiveUsers = configs.syncUsers ? false : configs.deleteInactiveUsers;
        changedConfigsState.deleteUsersNotMatchingRules = configs.syncUsers
          ? false
          : configs.deleteUsersNotMatchingRules;
        changedConfigsState.syncUsers = !configs.syncUsers;
        break;
      case "importUsageData":
        if (configs.importUsageData && !configs.syncUsers) {
          changedConfigsState.syncUsers = !configs.syncUsers;
        }

        changedConfigsState.importLicenses = !configs.syncUsers ? true : configs.importLicenses;
        changedConfigsState.importGroups = !configs.syncUsers ? true : configs.importGroups;

        changedConfigsState.importUsageData = !configs.importUsageData;
        break;
      case "isActive":
        changedConfigsState.isActive = !configs.isActive;
        break;
      case "deleteInactiveUsers":
        changedConfigsState.deleteInactiveUsers = !configs.deleteInactiveUsers;
        break;
      case "deleteUsersNotMatchingRules":
        changedConfigsState.deleteUsersNotMatchingRules = !configs.deleteUsersNotMatchingRules;
        break;
      case "importLicenses":
        changedConfigsState.importLicenses = !configs.importLicenses;
        break;
      case "importGroups":
        changedConfigsState.importGroups = !configs.importGroups;
        break;
    }
    setConfigs({ ...changedConfigsState });
  };

  const setDeleteInactiveUsersFieldValue = (value: boolean) => {
    setConfigs({ ...configs, deleteInactiveUsers: value });
  };

  const syncNowDisabled = configs.isSyncRunning || !configs.isActive || !configs.syncUsers;
  return (
    <>
      <div className={cn(styles["integration-configuration-block"], "scrollable-content")}>
        {isCreating && <Form.Block title="Integration type">{integrationName}</Form.Block>}
        <div className={cn(styles["options-block"], styles.divider)}>
          <div className={styles["options-title"]}>
            <span>
              <b>DATA IMPORT SETTINGS</b>
              (At least one selection must be made)
            </span>
          </div>
          <div className={styles["option-with-tooltip"]}>
            <ValidatedForm.CheckboxField
              setFieldValue={setFieldValue}
              toggle
              label="Users"
              value={configs.syncUsers}
              propertyName="syncUsers"
            />
            <Tooltip
              hoverable
              tooltipClassName="integraion-setting-tooltip"
              target={<Icon className="info circle" />}
              content={getSyncUsersTooltipMessage(IntegrationTypes.MsGraph)}
              position="top center"
              hideOnScroll
              showAlways
            />
          </div>
          {configs.syncUsers && (
            <>
              {isLicensesFilterEnabled && (
                <div className={styles["option-with-tooltip"]}>
                  <ValidatedForm.CheckboxField
                    setFieldValue={setFieldValue}
                    toggle
                    label="Licenses"
                    value={configs.importLicenses}
                    propertyName="importLicenses"
                  />
                  <Tooltip
                    hoverable
                    tooltipClassName="integraion-setting-tooltip"
                    target={<Icon className="info circle" />}
                    content={getImportLicensesTooltipMessage()}
                    position="top center"
                    hideOnScroll
                    showAlways
                  />
                </div>
              )}
              {isLicensesFilterEnabled && (
                <div className={styles["option-with-tooltip"]}>
                  <ValidatedForm.CheckboxField
                    setFieldValue={setFieldValue}
                    toggle
                    label="Groups"
                    value={configs.importGroups}
                    propertyName="importGroups"
                  />
                  <Tooltip
                    hoverable
                    tooltipClassName="integraion-setting-tooltip"
                    target={<Icon className="info circle" />}
                    content={getImportGroupsTooltipMessage()}
                    position="top center"
                    hideOnScroll
                    showAlways
                  />
                </div>
              )}
            </>
          )}
          <div className={styles["option-with-tooltip"]}>
            <ValidatedForm.CheckboxField
              setFieldValue={setFieldValue}
              toggle
              label="Usage"
              value={configs.importUsageData}
              propertyName="importUsageData"
            />
            <Tooltip
              hoverable
              tooltipClassName="integraion-setting-tooltip"
              target={<Icon className="info circle" />}
              content={getImportUsageDataTooltipMessage(IntegrationTypes.MsGraph)}
              position="top center"
              hideOnScroll
              showAlways
            />
          </div>
        </div>
        {isMsGraphSyncRulesEnabled && (
          <div className={cn(styles["options-block"], styles.divider)}>
            <MsGraphIntegrationFilterBuilder
              configs={configs}
              setIsValid={setIsValid}
              setConfigs={setConfigs}
              filterBuilderFormRef={filterBuilderFormRef}
            />
          </div>
        )}
        <div className={styles["options-block"]}>
          <div className={styles["option-with-tooltip"]}>
            <ValidatedForm.CheckboxField
              setFieldValue={setFieldValue}
              toggle
              label="Make active when finished"
              value={configs.isActive}
              propertyName="isActive"
            />
            <Tooltip
              hoverable
              tooltipClassName="integraion-setting-tooltip"
              target={<Icon className="info circle" />}
              content={
                "If this feature is enabled, the integration will be active and sync daily. If disabled, synchronizing with the BrainStorm platform will be paused."
              }
              position="top center"
              hideOnScroll
              showAlways
            />
          </div>
          {isMsGraphSyncRulesEnabled && (
            <>
              <div className={styles["option-with-tooltip"]}>
                <ValidatedForm.CheckboxField
                  toggle
                  setFieldValue={setFieldValue}
                  label="Automatically remove inactive users"
                  value={configs.deleteInactiveUsers}
                  propertyName="deleteInactiveUsers"
                />
                <Tooltip
                  hoverable
                  tooltipClassName="integraion-setting-tooltip"
                  target={<Icon className="info circle" />}
                  content={
                    "Inactive Users are defined as users that: have been deleted from MS Graph, don't have an active Microsoft license, don't have a UPN, or don't have a primary email address in their Microsoft Graph user profile."
                  }
                  position="top center"
                  hideOnScroll
                  showAlways
                />
              </div>
              {isMsGraphAutoRemoveUsersEnabled && (
                <div className={styles["option-with-tooltip"]}>
                  <ValidatedForm.CheckboxField
                    toggle
                    setFieldValue={setFieldValue}
                    label="Automatically remove users who do not fit the filter criteria"
                    value={configs.deleteUsersNotMatchingRules}
                    propertyName="deleteUsersNotMatchingRules"
                  />
                  <Tooltip
                    hoverable
                    tooltipClassName="integraion-setting-tooltip"
                    target={<Icon className="info circle" />}
                    content={
                      "Any users who do not fit the filter rules set above will automatically be removed. This will remove all user data and cannot be undone."
                    }
                    position="top center"
                    hideOnScroll
                    showAlways
                  />
                </div>
              )}
            </>
          )}
        </div>
        {!isMsGraphSyncRulesEnabled && configs.syncUsers && (
          <div className={styles["remove-users-section"]}>
            <div className={styles["remove-users-title"]}>
              <b>INACTIVE MICROSOFT USER SETTINGS</b>
              <Tooltip
                hoverable
                tooltipClassName="integraion-setting-tooltip"
                target={<Icon className="info circle" />}
                content={
                  "Inactive Users are defined as users that: have been deleted from MS Graph, don't have an active Microsoft license, don't have a UPN, or don't have a primary email address in their Microsoft Graph user profile."
                }
                position="top center"
                hideOnScroll
                showAlways
              />
            </div>
            <div className={styles["remove-users-radio-options"]}>
              <div>
                <Radio
                  id="dontRemoveUsers"
                  className={styles["licensed-radio-toggle"]}
                  label="Do not remove inactive users"
                  checked={!configs.deleteInactiveUsers}
                  onClick={() => setDeleteInactiveUsersFieldValue(false)}
                />
              </div>
              <div>
                <Radio
                  id="removeUsers"
                  className={styles["licensed-radio-toggle"]}
                  label="Automatically remove inactive users"
                  checked={configs.deleteInactiveUsers}
                  onClick={() => setDeleteInactiveUsersFieldValue(true)}
                />
              </div>
            </div>
          </div>
        )}
        {onSyncClick && (
          <div className={styles["sync-now-container"]}>
            <Button className={styles["button-container"]} basic fluid disabled={syncNowDisabled} onClick={onSyncClick}>
              <Icon className={styles["icon-container"]} size="small" name="sync" disabled={syncNowDisabled} />
              {configs.isSyncRunning ? "Syncing..." : "Sync Now"}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default MsGraphIntegrationConfiguration;
